import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PlusIcon, PencilIcon, TrashIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { IProjectAsset, AssetType, AssetTypeLabels, CharacterRoleLabels, CharacterRoleColors, getLabelsByLocale, Locale } from '@packages/global';
import { useAssets } from '../../hooks/useAssets';
import { useDialog } from '../../hooks/useDialog';
import { projectAssetApi } from '../../services/user/projectAsset.api';
import { useToast } from '../../stores/toastStore';
import AssetForm from './AssetForm';
import MediaPreviewModal from '../common/MediaPreviewModal';

interface AssetWithFavorite extends IProjectAsset {
  isFavorited?: boolean;
}

interface ProjectAssetsDrawerProps {
  isOpen: boolean;
  projectId?: string;
  episodeId?: string;
  onClose: () => void;
  title?: string;
  children?: React.ReactNode;
}

type TabType = 'character' | 'prop' | 'scene';

const tabConfig: Record<TabType, { labelKey: string; assetType: AssetType }> = {
  character: { labelKey: 'projectSettings:subject.character', assetType: AssetType.CHARACTER },
  prop: { labelKey: 'projectSettings:subject.prop', assetType: AssetType.PROP },
  scene: { labelKey: 'projectSettings:subject.scene', assetType: AssetType.SCENE }
};

export default function ProjectAssetsDrawer({ isOpen, projectId, episodeId, onClose, title, children }: ProjectAssetsDrawerProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const characterRoleLabels = getLabelsByLocale(CharacterRoleLabels, i18n.language as Locale);
  const [activeTab, setActiveTab] = useState<TabType>('character');
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<IProjectAsset | null>(null);
  const [favoriting, setFavoriting] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { assets, loading, createAsset, updateAsset, deleteAsset, getAssetsByType, loadAssets } = useAssets({ projectId, episodeId });
  const { confirm } = useDialog();
  const { success, error } = useToast();

  const currentAssetType = tabConfig[activeTab].assetType;
  const currentAssets = getAssetsByType(currentAssetType) as AssetWithFavorite[];

  const handleAdd = () => {
    setEditingAsset(null);
    setShowForm(true);
  };

  const handleEdit = (asset: IProjectAsset) => {
    setEditingAsset(asset);
    setShowForm(true);
  };

  const handleDelete = async (asset: IProjectAsset) => {
    const confirmed = await confirm(
      t('asset.deleteConfirm', { name: asset.name }),
      t('asset.deleteConfirmTitle'),
      'danger'
    );
    if (confirmed) {
      await deleteAsset(asset.id);
    }
  };

  const handleSubmit = async (data: any) => {
    const success = editingAsset
      ? await updateAsset(editingAsset.id, data)
      : await createAsset({ ...data, projectId, episodeId, assetType: currentAssetType });

    if (success) {
      setShowForm(false);
      setEditingAsset(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingAsset(null);
  };

  const handleFavorite = async (asset: AssetWithFavorite) => {
    if (favoriting || asset.isFavorited) return;
    setFavoriting(true);
    try {
      await projectAssetApi.favoriteAsset(asset.id);
      success(t('subject.favoritedSuccess'));
      asset.isFavorited = true;
    } catch (err: any) {
      error(err.message || t('subject.favoriteFailed'));
    } finally {
      setFavoriting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-[600px] bg-bg-surface shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border-subtle">
            <h2 className="text-xl font-semibold text-gray-50">{title || t('asset.subjectSetting')}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-border-subtle">
            <div className="flex px-6">
              {(Object.keys(tabConfig) as TabType[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    if (!showForm) {
                      setActiveTab(tab);
                    }
                  }}
                  disabled={showForm}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer ${activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                    } ${showForm ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {t(tabConfig[tab].labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {children ? (
              children
            ) : showForm ? (
              <AssetForm
                assetType={currentAssetType}
                editingAsset={editingAsset}
                onSubmit={handleSubmit}
                onCancel={handleFormClose}
              />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-50">
                    {t('asset.list', { type: t(tabConfig[activeTab].labelKey) })}
                  </h3>
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors cursor-pointer"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {t('asset.addType', { type: t(tabConfig[activeTab].labelKey) })}
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-12 text-gray-400">{t('common:loading')}</div>
                ) : currentAssets.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-400 mb-4">{t('asset.noTypeSetting', { type: t(tabConfig[activeTab].labelKey) })}</p>
                    <button
                      onClick={handleAdd}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      {t('asset.addFirstType', { type: t(tabConfig[activeTab].labelKey) })}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {currentAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="bg-bg-elevated rounded-lg p-4 hover:bg-bg-surface transition-colors relative group border border-border-subtle"
                      >
                        <div className="flex items-start gap-3">
                          {/* 缩略图 */}
                          <div className="flex-shrink-0">
                            {asset.conceptImageUrl ? (
                              <img
                                src={asset.conceptImageUrl}
                                alt={asset.name}
                                className="w-16 h-16 object-cover rounded border border-border-subtle cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setPreviewImage(asset.conceptImageUrl!)}
                              />
                            ) : (
                              <div className="w-16 h-16 bg-bg-surface rounded border border-border-subtle flex items-center justify-center">
                                <span className="text-xs text-gray-400">{t('asset.noImage')}</span>
                              </div>
                            )}
                          </div>

                          {/* 内容区 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-lg font-medium text-gray-50">{asset.name}</h4>
                              {asset.assetType === AssetType.CHARACTER && asset.role && (
                                <span
                                  className="px-2 py-0.5 text-xs rounded flex-shrink-0"
                                  style={{
                                    backgroundColor: `${CharacterRoleColors[asset.role]}20`,
                                    color: CharacterRoleColors[asset.role]
                                  }}
                                >
                                  {characterRoleLabels[asset.role]}
                                </span>
                              )}
                            </div>
                            {asset.prompt && (
                              <p className="text-sm text-gray-400 mb-3 line-clamp-2">{asset.prompt}</p>
                            )}
                            <div className="grid grid-cols-2 gap-2">
                              {asset.frontViewUrl && (
                                <div className="text-sm text-gray-400">
                                  <span className="text-gray-500">{t('asset.frontView')}:</span>{' '}
                                  <button onClick={() => setPreviewImage(asset.frontViewUrl!)} className="text-primary hover:text-primary-dark cursor-pointer">{t('asset.view')}</button>
                                </div>
                              )}
                              {asset.sideViewUrl && (
                                <div className="text-sm text-gray-400">
                                  <span className="text-gray-500">{t('asset.sideView')}:</span>{' '}
                                  <button onClick={() => setPreviewImage(asset.sideViewUrl!)} className="text-primary hover:text-primary-dark cursor-pointer">{t('asset.view')}</button>
                                </div>
                              )}
                              {asset.backViewUrl && (
                                <div className="text-sm text-gray-400">
                                  <span className="text-gray-500">{t('asset.backView')}:</span>{' '}
                                  <button onClick={() => setPreviewImage(asset.backViewUrl!)} className="text-primary hover:text-primary-dark cursor-pointer">{t('asset.view')}</button>
                                </div>
                              )}
                              {asset.detailImageUrl && (
                                <div className="text-sm text-gray-400">
                                  <span className="text-gray-500">{t('asset.detailView')}:</span>{' '}
                                  <button onClick={() => setPreviewImage(asset.detailImageUrl!)} className="text-primary hover:text-primary-dark cursor-pointer">{t('asset.view')}</button>
                                </div>
                              )}
                            </div>
                            {/* {asset.voiceReference && currentAssetType === AssetType.CHARACTER && (
                              <div className="mt-3 text-sm text-gray-400">
                                <span className="text-gray-500">音色参考:</span> {asset.voiceReference}
                              </div>
                            )} */}
                            {asset.description && (
                              <div className="mt-2 text-sm text-gray-400">
                                <span className="text-gray-500">{t('asset.descriptionLabel')}:</span>{' '}
                                <span className="line-clamp-2">{asset.description}</span>
                              </div>
                            )}
                          </div>

                          {/* 操作按钮 */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(asset)}
                              className="p-2 text-gray-400 hover:text-primary hover:bg-bg-surface rounded transition-colors cursor-pointer"
                              title={t('common:edit')}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(asset)}
                              className="p-2 text-gray-400 hover:text-error hover:bg-bg-surface rounded transition-colors cursor-pointer"
                              title={t('common:delete')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {(projectId || episodeId) && (
                          <button
                            onClick={() => handleFavorite(asset)}
                            disabled={favoriting || asset.isFavorited}
                            className={`absolute bottom-2 right-2 p-2 rounded bg-bg-elevated/80 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 ${asset.isFavorited ? 'hover:bg-bg-surface' : 'hover:bg-yellow-600/20'}`}
                            title={asset.isFavorited ? t('subject.favorited') : t('subject.favoriteToLibrary')}
                          >
                            {asset.isFavorited ? (
                              <StarIconSolid className="h-5 w-5 text-yellow-400" />
                            ) : (
                              <StarIcon className="h-5 w-5 text-yellow-400" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 图片预览弹窗 */}
      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('asset.assetImagePreview')}
      />
    </>
  );
}
